import { useMemo, useRef, useState } from 'react';
import { ApiError, type SettingField } from '@chicare/shared';

import { useAdminSettings, useSaveSettings, useUploadMedia } from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from './AdminError';
import { SettingFieldControl } from './SettingField';

/**
 * The settings console.
 *
 * Rendered entirely from the schema the API sends, so a new setting appears
 * here the moment it is declared in `SettingsRegistry` — no frontend change,
 * no risk of the console and the validator disagreeing about what is allowed.
 *
 * Two deliberate frictions:
 *
 *   1. Nothing saves until you press Save. A settings screen that writes on
 *      blur will eventually write something an admin was only looking at.
 *   2. Fields marked `requires_reason` (§9.11) demand a written note. It is
 *      the only record of why a threshold moved, six months from now.
 */
export function SettingsScreen() {
  const { data, isLoading, isError, error, refetch } = useAdminSettings();
  const save = useSaveSettings();
  const upload = useUploadMedia();

  const [activeGroup, setActiveGroup] = useState<string>('brand');
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [reason, setReason] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingImageField = useRef<SettingField | null>(null);

  const group = data?.groups.find((candidate) => candidate.key === activeGroup) ?? data?.groups[0];

  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((key) => draft[key] !== data?.values[key]),
    [draft, data],
  );

  const needsReason = useMemo(
    () =>
      dirtyKeys.some(
        (key) =>
          data?.groups
            .flatMap((candidate) => candidate.fields)
            .find((field) => field.key === key)?.requires_reason,
      ),
    [dirtyKeys, data],
  );

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  if (isError) {
    return <AdminError title="We couldn't load settings" error={error} onRetry={() => refetch()} />;
  }

  const handleSave = () => {
    const values = Object.fromEntries(dirtyKeys.map((key) => [key, draft[key]]));

    save.mutate(
      { values, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          setDraft({});
          setReason('');
        },
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-6">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Settings</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">
          Everything ChiCare can change without a deploy. The rules that keep buyers and pharmacies
          safe are deliberately absent — there is no switch here that turns off the ledger check,
          gates a counterfeit refund, or lets a buyer hold a cash balance.
        </p>
      </header>

      {isLoading || !data || !group ? (
        <SettingsSkeleton />
      ) : (
        <div className="grid gap-6 md:grid-cols-[220px_1fr] md:items-start">
          {/*
            `min-w-0` is load-bearing. A grid item defaults to `min-width: auto`,
            so without it this nav grows to fit eleven nowrap tabs — about
            1045px — and the inner `overflow-x-auto` never engages because the
            container is already as wide as its content. The whole document then
            widens, and every media query below that width stops applying.
          */}
          <nav aria-label="Settings sections" className="min-w-0 md:sticky md:top-4">
            <ul className="m-0 flex list-none gap-1.5 overflow-x-auto p-0 scrollbar-none md:flex-col md:overflow-visible">
              {data.groups.map((candidate) => {
                const dirtyInGroup = candidate.fields.filter((field) =>
                  dirtyKeys.includes(field.key),
                ).length;

                return (
                  <li key={candidate.key}>
                    <button
                      type="button"
                      onClick={() => setActiveGroup(candidate.key)}
                      className={cn(
                        'flex w-full items-center gap-2 whitespace-nowrap rounded-btn px-3 py-2.5 text-left text-[13px]',
                        candidate.key === group.key
                          ? 'bg-blue-tint font-bold text-blue'
                          : 'text-muted hover:bg-paper',
                      )}
                    >
                      {candidate.label}
                      {dirtyInGroup > 0 ? (
                        <span className="ml-auto grid h-[18px] min-w-[18px] place-items-center rounded-pill bg-verified px-1 text-[10px] font-bold text-white">
                          {dirtyInGroup}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="min-w-0">
            <Card>
              <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
                {group.label}
              </h2>
              <p className="mt-1 text-xs text-muted">{group.help}</p>

              <div className="mt-2 divide-y divide-line-2">
                {group.fields.map((field) => (
                  <SettingFieldControl
                    key={field.key}
                    field={field}
                    value={field.key in draft ? draft[field.key] : data.values[field.key]}
                    media={
                      field.type === 'image'
                        ? data.media[String(draft[field.key] ?? data.values[field.key])]
                        : undefined
                    }
                    error={fieldErrors[field.key]?.[0]}
                    dirty={dirtyKeys.includes(field.key)}
                    onChange={(value) => setDraft((current) => ({ ...current, [field.key]: value }))}
                    onPickImage={(target) => {
                      pendingImageField.current = target;
                      fileInput.current?.click();
                    }}
                  />
                ))}
              </div>
            </Card>

            {dirtyKeys.length > 0 ? (
              <SaveBar
                count={dirtyKeys.length}
                needsReason={needsReason}
                reason={reason}
                onReason={setReason}
                saving={save.isPending}
                error={
                  save.error instanceof ApiError && !save.error.isValidation
                    ? save.error.message
                    : (fieldErrors.reason?.[0] ?? fieldErrors.settings?.[0])
                }
                onSave={handleSave}
                onDiscard={() => {
                  setDraft({});
                  setReason('');
                  save.reset();
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          const field = pendingImageField.current;
          event.target.value = '';

          if (!file || !field) return;

          const asset = await upload.mutateAsync({
            file,
            collection: field.key.replace(/\./g, '/'),
            alt: field.label,
          });

          setDraft((current) => ({ ...current, [field.key]: asset.id }));
        }}
      />
    </div>
  );
}

/**
 * A sticky bar, not an auto-save.
 *
 * These fields decide when buyers are charged and what shelf life is
 * acceptable; committing them on blur would eventually commit something an
 * admin was only reading.
 */
function SaveBar({
  count,
  needsReason,
  reason,
  onReason,
  saving,
  error,
  onSave,
  onDiscard,
}: {
  count: number;
  needsReason: boolean;
  reason: string;
  onReason: (value: string) => void;
  saving: boolean;
  error?: string;
  onSave: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="sticky bottom-4 mt-4 rounded-card border border-blue bg-white p-3.5 shadow-md">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[13px] font-bold">
          {count} unsaved change{count === 1 ? '' : 's'}
        </span>
        <span className="ml-auto flex gap-2">
          <Button variant="grey" size="sm" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            loading={saving}
            disabled={needsReason && reason.trim().length < 3}
          >
            Save changes
          </Button>
        </span>
      </div>

      {needsReason ? (
        <div className="mt-3">
          <label
            htmlFor="settings-reason"
            className="mb-[5px] flex items-center gap-2 text-[11px] font-bold text-muted"
          >
            <Icon name="alert" size={13} className="text-caution" />
            Why are you making this change? <span className="text-danger">required</span>
          </label>
          <textarea
            id="settings-reason"
            rows={2}
            value={reason}
            onChange={(event) => onReason(event.target.value)}
            placeholder="e.g. Paystack confirmed the settlement cut-off is 22:00, not 23:00."
            className="w-full rounded-inp border border-line bg-white px-3 py-2.5 text-[13px] outline-none placeholder:text-faint focus:border-blue focus:ring-2 focus:ring-blue/15"
          />
          <p className="mt-1.5 text-[11px] text-faint">
            Recorded against you with a timestamp. In six months this note is the only thing that
            explains the change.
          </p>
        </div>
      ) : null}

      {error ? <p className="mt-2 text-[11px] font-medium text-danger">{error}</p> : null}
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="grid gap-6 md:grid-cols-[220px_1fr]">
      <div className="space-y-1.5">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="h-9 w-full" radius="md" />
        ))}
      </div>
      <Card>
        <Skeleton className="h-5 w-40" />
        <div className="mt-4 space-y-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-2.5 w-28" />
              <Skeleton className="h-[42px] w-full" radius="md" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
